import React from 'react';
import { formatPhoneForDisplay } from '../../../helpers/phoneUtils';
import { useInvitationContext } from '../context/InvitationContext';
import { useT } from '../../../i18n/admin';
import { PhoneStepShell } from './AccessFormPhone';
import { PhoneCardSheet, PhoneReviewStep, type CardValue, type ReviewData } from './NewPassPhoneSteps';
import { isPassFeeService } from './serviceBooking';

const EMPTY_CARD: CardValue = { cardNumber: '', cardHolder: '', expiryDate: '', cvv: '' };

/**
 * SAM-944 / SAM-1171 — step 6 at 375 px: Review in one screen (canvas v256 x2730, card sheet x3185/x3640).
 * It replaces the Payment and Confirm views on a phone. A pass_fee service is paid by the guest through a payment
 * link: Create pass runs the same `onConfirm` (handleConfirm) as the desktop Confirm, and the link shows on the
 * Pass created screen. Only an engine service booking still types a card, in a sheet. Prices are the server's:
 * nothing is computed or shown from here.
 */
export const PhoneReviewPage: React.FC<{
  requiresPayment: boolean;
  /** A service booking: its price is the server's, and an empty card is allowed. */
  cardOptional?: boolean;
  error?: string | null;
  isLoading?: boolean;
  onConfirm: () => void;
  onBack: () => void;
  onClose: () => void;
}> = ({ requiresPayment, cardOptional, error, isLoading, onConfirm, onBack, onClose }) => {
  const { invitation, updateInvitation } = useInvitationContext();
  const { t, fmt } = useT();
  const [cardOpen, setCardOpen] = React.useState(Boolean(error));

  const mg = invitation?.guests?.mainGuest;
  const name = `${mg?.firstName || ''} ${mg?.lastName || ''}`.trim();
  const sb = invitation?.serviceBooking;
  const service = invitation?.selectedService;
  const serviceName = sb?.serviceName || service?.name_en || service?.name || '';
  const paidByLink = isPassFeeService(service);

  const dur = invitation?.duration;
  const day = (iso?: string | null) => {
    const d = iso ? new Date(iso) : null;
    return d && !Number.isNaN(d.getTime()) ? fmt.date(d) : '';
  };
  const date = sb
    ? [day(sb.date), sb.time].filter(Boolean).join(' · ')
    : dur?.mode === 'multiple'
      ? [day(dur.startDate), day(dur.endDate)].filter(Boolean).join(' - ')
      : [day(dur?.date), t('booking.phone.allDay')].filter(Boolean).join(' · ');

  const adults = invitation?.guests?.companions?.adults || 0;
  const kids = invitation?.guests?.companions?.childrens || 0;
  const guests = sb
    ? [t(sb.quantities.reduce((n, q) => n + q.quantity, 0) === 1 ? 'booking.guests.one' : 'booking.guests.many', { n: sb.quantities.reduce((n, q) => n + q.quantity, 0) })]
    : [
        adults ? t(adults === 1 ? 'booking.phone.adultN' : 'booking.phone.adultsN', { n: adults }) : '',
        kids ? t(kids === 1 ? 'booking.phone.childN' : 'booking.phone.childrenN', { n: kids }) : '',
      ].filter(Boolean);

  // The labels picked in the destination step: estate group · estate for a residence, the business name for a business.
  const destination = [
    ...(invitation?.location?.estate_group_id ?? []),
    ...(invitation?.location?.estate_id ?? []),
  ]
    .map((o) => o?.label)
    .filter((l, i, all): l is string => Boolean(l) && all.indexOf(l) === i)
    .join(' · ');

  const data: ReviewData = {
    name,
    phone: mg?.phoneNumber ? formatPhoneForDisplay(mg.phoneNumber) : '',
    destination,
    service: serviceName,
    payment: paidByLink ? t('passConfirm.link') : undefined,
    date,
    guests,
  };

  const mainLabel = requiresPayment ? t('common.continue') : t('booking.phone.createPass');
  const main = () => (requiresPayment ? setCardOpen(true) : onConfirm());
  const card = (invitation?.creditCard as CardValue | undefined) ?? EMPTY_CARD;

  return (
    <div className="relative h-full">
      <PhoneStepShell step={6} onBack={onBack} onClose={onClose} onContinue={main} continueLabel={mainLabel} loading={isLoading && !requiresPayment}>
        <PhoneReviewStep data={data} error={cardOpen ? null : error} />
      </PhoneStepShell>
      {requiresPayment && cardOpen && (
        <PhoneCardSheet
          value={card}
          onChange={(v) => updateInvitation({ creditCard: v })}
          payLabel={isLoading ? '…' : t('booking.phone.createPass')}
          onPay={onConfirm}
          onClose={() => setCardOpen(false)}
          busy={isLoading}
          error={error}
          optional={cardOptional}
        />
      )}
    </div>
  );
};
